/**
 * Approval workflows: the list.
 *
 * One row per workflow: its name, how many steps, and which documents follow
 * it. Click one to define it. "New workflow" makes another. The owner's words,
 * 2026-09-20: "I would expect to see just a list of approvals defined by name
 * and number of steps and maybe a document type. Then I would expect to click
 * that approval and dig into it deeper." The first version of this page opened
 * INSIDE one workflow and hid the way to make another behind "Show example
 * workflows", so it read as though only one could exist.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { sortSteps, useApprovalSteps, useApprovalWorkflows } from './approvalsData';
import { ROLE_DISPLAY_NAMES, isRole } from '../../types/roles';
import { useTaxonomy } from '../admin/useTaxonomy';
import { coverIds, coversAnything, fallsToCatchAll } from './coverage';
import { RouteWhatIf } from './RouteWhatIf';
import { isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import styles from '../../shared/Page.module.css';
import own from './Approvals.module.css';

interface Named { id: string; name: string }

function names(ids: string[], all: readonly Named[], none: string): string {
  if (ids.length === 0) return none;
  return ids.map((id) => all.find((x) => x.id === id)?.name ?? 'a removed item').join(', ');
}

/** One workflow's steps, opened in place under its row. */
function RouteRow({ workflowId, name, columns }: { workflowId: string; name: string; columns: number }) {
  const steps = useApprovalSteps(workflowId);
  const ordered = sortSteps(steps.data?.steps ?? []);
  return (
    <tr>
      <td colSpan={columns} className={own.routeCell}>
        {steps.isLoading ? <Spinner label={`Loading the route of ${name}`} />
          : steps.error ? <p className={own.error}>The route could not be loaded. Open the workflow to try again.</p>
            : ordered.length === 0 ? <p className={own.itemMeta}>No steps yet, so documents it covers skip it.</p>
              : (
                <ol className={own.exampleSteps} aria-label={`The route of ${name}`}>
                  {ordered.map((s, i) => (
                    <li key={s.stepId}>
                      <span className={own.exampleOrder} aria-hidden="true">{i + 1}</span>
                      <span><span className="vh">Step {i + 1}: </span>{s.name}
                        <span className={own.exampleRole}> · {s.roleId && isRole(s.roleId) ? ROLE_DISPLAY_NAMES[s.roleId] : 'No role assigned'}</span>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
      </td>
    </tr>
  );
}

export function ApprovalWorkflowsPage() {
  const { session } = useAuth();
  const workflows = useApprovalWorkflows(Boolean(session));
  const typeItems = useTaxonomy('documentType', Boolean(session));
  const departmentItems = useTaxonomy('department', Boolean(session));
  const types = (typeItems.data ?? []).filter((t) => t.isActive);
  const departments = (departmentItems.data ?? []).filter((d) => d.isActive);
  const [open, setOpen] = useState<string | null>(null);
  const [showing, setShowing] = useState<'active' | 'retired' | 'all'>('active');

  if (workflows.isLoading) return <Spinner label="Loading approval workflows" />;

  /**
   * RETIRING A WORKFLOW USED TO DELETE IT FROM THE PRODUCT. This page filtered
   * to the active ones with no way to see the rest, and retiring navigates
   * straight here — so a retired workflow's own page, which says it is retired
   * and offers "Bring it back", could only be reached by pasting a URL. The
   * data was already here: the query asks for `includeInactive`.
   *
   * "In use" and "Not in use" are about ROUTING, not retirement: a workflow in
   * use is the catch-all or covers something, and one not in use is active but
   * nothing routes to it yet. Retired is a third thing and now says so.
   *
   * The one for everything else goes last: it is what the others fall back to.
   */
  // Copied before sorting: `sort` works in place, and this array belongs to
  // the query cache. Sorting it directly reorders what every other reader of
  // that cache sees, which is the kind of bug that shows up three screens away.
  const all = [...(workflows.data?.workflows ?? [])]
    .sort((a, b) => Number(Boolean(a.isDefault)) - Number(Boolean(b.isDefault)) || a.name.localeCompare(b.name));
  const retiredCount = all.filter((w) => w.isActive === false).length;
  const list = all.filter((w) => (showing === 'retired' ? w.isActive === false : showing === 'all' ? true : w.isActive !== false));
  /**
   * WHAT ROUTING SEES, not what this page is showing. Both of these answer
   * questions about the organization — "is there a workflow for everything
   * else?", "what falls to it?" — and reading them off the filtered rows made
   * the Retired tab warn that no workflow took everything else while one
   * plainly did. A filter changes the view, never the facts.
   */
  const inPlay = all.filter((w) => w.isActive !== false);
  const everythingElse = inPlay.find((w) => w.isDefault) ?? null;
  const left = fallsToCatchAll(inPlay, types, departments);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerRow}>
        <div>
          <h1 className={styles.title}>Approval workflows</h1>
          <p className={styles.subtitle}>
            Each workflow is a chain of approval steps for some kinds of document. A submitted
            document follows the most specific workflow that covers it, or the catch-all. When a
            document would otherwise use the catch-all, its author may choose another workflow you
            have made available and say why. Administrators may override routing.
          </p>
        </div>
        <div className={styles.actions}>
          {retiredCount > 0 && (
            <fieldset className={own.showing}>
              <legend className="vh">Which workflows to show</legend>
              {([['active', 'In play'], ['retired', `Retired (${retiredCount})`], ['all', 'All']] as const).map(([value, label]) => (
                <label key={value} className={`${own.showingOne} ${showing === value ? own.showingOn : ''}`}>
                  <input type="radio" name="showing" className="vh" checked={showing === value} onChange={() => setShowing(value)} />
                  <span>{label}</span>
                </label>
              ))}
            </fieldset>
          )}
          <Link to="/admin/approval-workflows/new" className={`${styles.btn} ${styles.btnPrimary}`}>New workflow</Link>
        </div>
        </div>
      </header>

      <div className={styles.body}>
        {workflows.error && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
            {isCallFailure(workflows.error) || workflows.error instanceof Error ? workflows.error.message : 'Could not load workflows.'}
          </div>
        )}

        {!workflows.error && list.length === 0 && showing !== 'active' ? (
          <section className={styles.section}>
            <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">No retired workflows.</div>
          </section>
        ) : !workflows.error && list.length === 0 ? (
          <section className={styles.section}>
            <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">
              No workflow exists yet. Until one does, a submitted document gets a single Approval
              step held by the Approver role.
            </div>
            <div className={styles.actions}>
              <Link to="/admin/approval-workflows/new" className={`${styles.btn} ${styles.btnPrimary}`}>Create your first workflow</Link>
            </div>
          </section>
        ) : !workflows.error && (
          <section className={styles.section}>
            <div className={own.tableWrap}>
              <table className={own.table}>
                <caption className="vh">Approval workflows</caption>
                <thead>
                  <tr><th scope="col">Workflow</th><th scope="col">Steps</th><th scope="col">Document types</th><th scope="col">Departments</th><th scope="col">Status</th><th scope="col"><span className="vh">Route</span></th></tr>
                </thead>
                <tbody>
                  {list.map((w) => {
                    const c = coverIds(w);
                    const retired = w.isActive === false;
                    const used = Boolean(w.isDefault) || coversAnything(w);
                    const steps = w.stepCount ?? 0;
                    return [
                      <tr key={w.workflowId}>
                        <th scope="row">
                          <Link to={`/admin/approval-workflows/${w.workflowId}`} className={own.rowLink}>{w.name}</Link>
                          {w.description && <div className={`${own.itemMeta} ${own.oneLine}`} title={w.description}>{w.description}</div>}
                        </th>
                        <td className={steps === 0 ? own.cellWarn : undefined}>{steps === 0 ? 'No steps yet' : steps === 1 ? '1 step' : `${steps} steps`}</td>
                        <td>{w.isDefault ? 'Everything else' : names(c.types, types, c.departments.length ? 'Any type' : 'None chosen')}</td>
                        <td>{w.isDefault ? 'Everything else' : names(c.departments, departments, c.types.length ? 'Any department' : 'None chosen')}</td>
                        <td>
                          {retired ? <span className={`${own.pill} ${own.pillOff}`}>Retired</span>
                            : steps === 0 ? <span className={`${own.pill} ${own.pillWarn}`}>Skipped: no steps</span>
                              : used ? <span className={`${own.pill} ${own.pillOn}`}>In use</span>
                                : <span className={`${own.pill} ${own.pillOff}`}>Not in use</span>}
                          {w.authorsMayChoose === false && <div className={own.itemMeta}>Not offered to authors</div>}
                        </td>
                        <td>
                          <button type="button" className={own.linkBtn} aria-expanded={open === w.workflowId}
                            aria-label={`${open === w.workflowId ? 'Hide' : 'Show'} the route of ${w.name}`}
                            onClick={() => setOpen(open === w.workflowId ? null : w.workflowId)}>
                            {open === w.workflowId ? 'Hide route' : 'Show route'}
                          </button>
                        </td>
                      </tr>,
                      open === w.workflowId && <RouteRow key={`${w.workflowId}-route`} workflowId={w.workflowId} name={w.name} columns={6} />,
                    ];
                  })}
                </tbody>
              </table>
            </div>
            {!everythingElse && (
              <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
                No workflow takes everything else, so a document no workflow covers gets a single
                Approval step. Open a workflow and choose &ldquo;Use for everything else&rdquo;.
              </div>
            )}
            {everythingElse && left.types.length > 0 && left.types.length < types.length && (
              <p className={own.itemMeta}>
                Document types no workflow names on its own, so they follow &ldquo;{everythingElse.name}&rdquo;:{' '}
                {left.types.map((t) => t.name).join(', ')}.
              </p>
            )}
          </section>
        )}

        {list.length > 0 && <RouteWhatIf types={types} departments={departments} />}
      </div>
    </>
  );
}
